import * as React from 'react'
import type { ColumnDef } from '@tanstack/react-table'

import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header'
import {
  DataTableCellNumber,
  DataTableCellText,
  DataTableCellTimestamp,
} from '@/components/data-table/data-table-cell'
import type { DataTableFilterField } from '@/components/data-table/types'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { createSchema, field } from '@/lib/store/schema'
import { columnFiltersToFilterConditions } from '@/features/calls/utils/mergeFilters'
import { TabRefreshButton } from '@/components/panel/TabRefreshButton'
import { DatabaseZap, Layers } from 'lucide-react'
import {
  useIngestionRunFacetsQuery,
  useIngestionRunsQuery,
} from '../hooks/useIngestionQueries'
import type { IngestionRunRecord } from '../types'
import {
  humanizeKey,
  toTranslatedOptions,
  translateEntity,
  translateEntityFilter,
  translateSource,
  translateStatus,
} from '../translations'

const TABLE_ID = 'ingestion-runs'
const SORT_FIELD = 'started_at' as const
const DEFAULT_COLUMN_SORTING = [{ id: SORT_FIELD, desc: true }]

const countCell = (value: number | null | undefined) =>
  typeof value === 'number' ? <DataTableCellNumber value={value} /> : '-'

const ingestionRunsSchema = createSchema({
  source: field.array(field.string()).delimiter(',').default([]),
  entity: field.array(field.string()).delimiter(',').default([]),
  // oxlint-disable-next-line unicorn/no-array-sort -- schema field builder, not Array.prototype.sort
  sort: field.sort().default({ id: SORT_FIELD, desc: true }),
})

const columns: ColumnDef<IngestionRunRecord>[] = [
  {
    accessorKey: 'filename',
    size: 240,
    minSize: 240,
    maxSize: 240,
    enableResizing: false,
    enableSorting: false,
    meta: {
      headerClassName: 'w-[240px] min-w-[240px] max-w-[240px]',
      cellClassName:
        'w-[240px] min-w-[240px] max-w-[240px] whitespace-normal overflow-visible text-clip',
    },
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="File" />
    ),
    cell: ({ row }) => {
      const value = row.original.filename
      return value ? (
        <span className="block whitespace-normal break-words">{value}</span>
      ) : (
        '-'
      )
    },
  },
  {
    accessorKey: 'source',
    enableSorting: false,
    size: 140,
    minSize: 120,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source" />
    ),
    cell: ({ row }) => (
      <DataTableCellText value={translateSource(row.original.source)} />
    ),
  },
  {
    accessorKey: 'entity',
    enableSorting: false,
    size: 110,
    minSize: 96,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Data type" />
    ),
    cell: ({ row }) => (
      <DataTableCellText value={translateEntity(row.original.entity)} />
    ),
  },
  {
    accessorKey: SORT_FIELD,
    size: 180,
    minSize: 160,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Started" />
    ),
    cell: ({ row }) => {
      return <DataTableCellTimestamp date={row.original.started_at} />
    },
  },
  {
    accessorKey: 'trigger_source',
    enableSorting: false,
    size: 100,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Trigger" />
    ),
    cell: ({ row }) => (
      <DataTableCellText
        value={
          row.original.trigger_source
            ? humanizeKey(row.original.trigger_source)
            : '-'
        }
      />
    ),
  },
  {
    accessorKey: 'status',
    enableSorting: false,
    size: 200,
    minSize: 176,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Outcome" />
    ),
    cell: ({ row }) => (
      <DataTableCellText
        value={translateStatus(row.original.status, row.original.result)}
      />
    ),
  },
  {
    id: 'persisted_output_count',
    size: 90,
    minSize: 80,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Persisted" />
    ),
    cell: ({ row }) => countCell(row.original.result?.persisted_output_count),
  },
  {
    id: 'rejected_source_record_count',
    size: 80,
    minSize: 72,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Rejected" />
    ),
    cell: ({ row }) =>
      countCell(row.original.result?.rejected_source_record_count),
  },
  {
    id: 'skipped_source_record_count',
    size: 78,
    minSize: 70,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Skipped" />
    ),
    cell: ({ row }) =>
      countCell(row.original.result?.skipped_source_record_count),
  },
  {
    id: 'failed_record_count',
    enableSorting: false,
    size: 80,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Failed" />
    ),
    cell: ({ row }) =>
      countCell(
        row.original.result?.failed_record_count ??
          row.original.result?.system_error_source_record_count
      ),
  },
]

// The set of fields the filter rail can offer. Options are populated per-org
// from the facets query so a customer never sees a source or data type their
// organization does not ingest.
const FILTER_FIELD_IDS = ['source', 'entity'] as const

function IngestionRunsInner() {
  const storeState = useFilterState()
  const { resetAllFilters } = useFilterActions()
  const facetsQuery = useIngestionRunFacetsQuery()

  // Filter options are the org's own distinct values (translated), not the
  // full platform enum — building them from the facets endpoint keeps other
  // orgs' sources and data types out of this customer's dropdowns.
  const filterFields = React.useMemo<
    DataTableFilterField<IngestionRunRecord>[]
  >(() => {
    const sources = facetsQuery.data?.sources ?? []
    const entities = facetsQuery.data?.entities ?? []
    return [
      {
        label: 'Source',
        value: 'source',
        type: 'checkbox',
        defaultOpen: true,
        triggerIcon: DatabaseZap,
        options: toTranslatedOptions(sources, translateSource),
      },
      {
        label: 'Data type',
        value: 'entity',
        type: 'checkbox',
        defaultOpen: true,
        triggerIcon: Layers,
        options: toTranslatedOptions(entities, translateEntityFilter),
      },
    ]
  }, [facetsQuery.data])

  // Only real filter fields become query params — exclude store metadata
  // (sort, selected-row uuid) so unrelated state changes don't churn the key.
  const apiFilters = React.useMemo(() => {
    const ids = new Set<string>(FILTER_FIELD_IDS)
    const columnFilters = Object.entries(storeState ?? {})
      .filter(([key, value]) => {
        if (!ids.has(key)) return false
        if (value === null || value === undefined) return false
        if (Array.isArray(value)) return value.length > 0
        return String(value).trim().length > 0
      })
      .map(([id, value]) => ({ id, value }))
    return columnFiltersToFilterConditions(columnFilters)
  }, [storeState])

  const query = useIngestionRunsQuery({
    limit: 25,
    sort_by: SORT_FIELD,
    sort_order:
      (storeState.sort as { id: string; desc: boolean } | null)?.desc === false
        ? 'asc'
        : 'desc',
    filters: apiFilters.length > 0 ? apiFilters : undefined,
  })

  const data = React.useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data]
  )

  const getRowId = React.useCallback((row: IngestionRunRecord) => row.id, [])

  const ToolbarActions = React.useMemo(
    () => (
      <TabRefreshButton
        onRefresh={query.refetch}
        isFetching={query.isFetching}
      />
    ),
    [query.refetch, query.isFetching]
  )

  return (
    <DataTableInfinite<IngestionRunRecord, unknown>
      tableId={TABLE_ID}
      columns={columns}
      data={data}
      filterFields={filterFields}
      manualFiltering
      manualSorting
      defaultColumnSorting={DEFAULT_COLUMN_SORTING}
      isRefetching={query.isPlaceholderData}
      enableRowDetails={false}
      isError={query.isError}
      error={query.error}
      entityLabel="imports"
      onClearFilters={resetAllFilters}
      isLoading={query.isLoading}
      isFetching={query.isFetching}
      hasNextPage={query.hasNextPage}
      fetchNextPage={query.fetchNextPage}
      refetch={query.refetch}
      totalRowsFetched={data.length}
      dataUpdatedAt={query.dataUpdatedAt}
      getRowId={getRowId}
      toolbarActions={ToolbarActions}
    />
  )
}

/**
 * Imports — read-only history of file imports and Cobalt syncs for the signed-in
 * org, rendered on the shared infinite data table. Translated source / data
 * type / trigger / outcome plus persisted / rejected / skipped / failed counts per run,
 * filterable by Source and Data type (options scoped to the org's own ingested
 * values).
 */
export function IngestionRunsPage() {
  const adapter = useNuqsAdapter(ingestionRunsSchema.definition, {
    id: TABLE_ID,
    history: 'replace',
    namespaceUrlKeys: true,
  })

  return (
    <DataTableStoreProvider adapter={adapter}>
      <IngestionRunsInner />
    </DataTableStoreProvider>
  )
}
